import React, { useState, useEffect } from 'react';
import emailjs from '@emailjs/browser';
import '../styles/form/AgenciesJoin.scss';

const AgenciesJoin = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    agencyName: '',
    hearAboutUs: '',
    hearAboutUsOther: '',
    contactEmail: '',
    phone: '',

    // Step 2: Details - simplified
    additionalInfo: '',
    staffingAreas: ''
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [animationStep, setAnimationStep] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [heroActiveStep, setHeroActiveStep] = useState(1);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);

    setIsVisible(true);
    const timer = setTimeout(() => setAnimationStep(1), 500);
    return () => clearTimeout(timer);
  }, []);

  // Auto-animate hero steps every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setHeroActiveStep(prev => prev >= 3 ? 1 : prev + 1);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpen && !event.target.closest('.custom-select')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const hearAboutUsOptions = [
    {
      value: 'LinkedIn',
      description: 'Professional network',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>,
      color: '#0077b5'
    },
    {
      value: 'Sales Team Call',
      description: 'Our team reached out to you',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
      color: '#10b981'
    },
    {
      value: 'Brochure / Mail',
      description: 'Physical mail or printed material',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>,
      color: '#f59e0b'
    },
    {
      value: 'Google Search',
      description: 'Found us through a search engine',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>,
      color: '#4285f4'
    },
    {
      value: 'Referral / Word of Mouth',
      description: 'Recommended by someone you know',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
      color: '#8b5cf6'
    },
    {
      value: 'Website',
      description: 'Found our website directly',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>,
      color: '#1e3a8a'
    },
    {
      value: 'Other',
      description: 'Tell us how you found us',
      icon: <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>,
      color: '#64748b'
    }
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    return phoneRegex.test(phone);
  };

  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/[^\d]/g, '');
    const phoneNumberLength = phoneNumber.length;
    
    if (phoneNumberLength < 4) return phoneNumber;
    if (phoneNumberLength < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  const handleInputChange = (field, value) => {
    if (field === 'phone') {
      const formattedPhone = formatPhoneNumber(value);
      if (formattedPhone.replace(/[^\d]/g, '').length <= 10) {
        setFormData(prev => ({ ...prev, [field]: formattedPhone }));
      }
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.agencyName.trim()) {
      newErrors.agencyName = 'Please share your agency name with us';
    }
    
    if (!formData.hearAboutUs) {
      newErrors.hearAboutUs = 'Please let us know how you heard about us';
    }
    if (formData.hearAboutUs === 'Other' && !formData.hearAboutUsOther.trim()) {
      newErrors.hearAboutUsOther = 'Please specify how you heard about us';
    }
    
    if (!formData.contactEmail.trim()) {
      newErrors.contactEmail = 'An email address helps us connect with you';
    } else if (!validateEmail(formData.contactEmail)) {
      newErrors.contactEmail = 'Please provide a valid email format';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'A contact number helps us reach you efficiently';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please use format: (213) 555-0123';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleStep1Submit = () => {
    if (validateStep1()) {
      setCurrentStep(2);
      setAnimationStep(0);
      setTimeout(() => setAnimationStep(1), 100);
    }
  };

  const handleFinalSubmit = async () => {
    setIsSubmitting(true);

    try {
      const hearAboutUsValue = formData.hearAboutUs === 'Other' ? formData.hearAboutUsOther : formData.hearAboutUs;

      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          subject: `New Agency Application — ${formData.agencyName}`,
          message_html: `
<div style="font-family: 'Segoe UI', Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <div style="background: linear-gradient(135deg, #174686 0%, #1e5aa8 100%); padding: 30px; border-radius: 12px 12px 0 0; text-align: center;">
    <h1 style="color: #ffffff; margin: 0; font-size: 22px; font-weight: 600;">New Agency Application</h1>
    <p style="margin: 8px 0 0; font-size: 14px;"><a href="https://motivehomecare.com" style="color: #ffffff; text-decoration: underline;">motivehomecare.com</a></p>
  </div>
  <div style="background: #ffffff; padding: 28px 30px; border-left: 1px solid #e5e7eb; border-right: 1px solid #e5e7eb;">
    <div style="background: #f0f6ff; border-left: 4px solid #174686; padding: 16px 20px; border-radius: 0 8px 8px 0; margin-bottom: 24px;">
      <h2 style="color: #174686; margin: 0 0 4px; font-size: 18px; font-weight: 600;">${formData.agencyName}</h2>
      <p style="color: #6b7280; margin: 0; font-size: 13px;">Agency Partnership Request</p>
    </div>
    <table style="width: 100%; border-collapse: collapse;">
      <tr>
        <td style="padding: 12px 0; border-bottom: 1px solid #f3f4f6; color: #6b7280; font-size: 13px; width: 160px; vertical-align: top;">Email</td>
        <td style="padding: 12px 0; border-bottom: 1px solid #f3f4f6; color: #111827; font-size: 14px; font-weight: 500;">${formData.contactEmail}</td>
      </tr>
      <tr>
        <td style="padding: 12px 0; border-bottom: 1px solid #f3f4f6; color: #6b7280; font-size: 13px; vertical-align: top;">Phone</td>
        <td style="padding: 12px 0; border-bottom: 1px solid #f3f4f6; color: #111827; font-size: 14px; font-weight: 500;">${formData.phone}</td>
      </tr>
      <tr>
        <td style="padding: 12px 0; border-bottom: 1px solid #f3f4f6; color: #6b7280; font-size: 13px; vertical-align: top;">Heard about us via</td>
        <td style="padding: 12px 0; border-bottom: 1px solid #f3f4f6; color: #111827; font-size: 14px; font-weight: 500;">${hearAboutUsValue}</td>
      </tr>
      <tr>
        <td style="padding: 12px 0; border-bottom: 1px solid #f3f4f6; color: #6b7280; font-size: 13px; vertical-align: top;">Staffing Areas</td>
        <td style="padding: 12px 0; border-bottom: 1px solid #f3f4f6; color: #111827; font-size: 14px; font-weight: 500;">${formData.staffingAreas || 'Not specified'}</td>
      </tr>
      <tr>
        <td style="padding: 12px 0; border-bottom: 1px solid #f3f4f6; color: #6b7280; font-size: 13px; vertical-align: top;">Additional Details</td>
        <td style="padding: 12px 0; border-bottom: 1px solid #f3f4f6; color: #111827; font-size: 14px; font-weight: 500;">${formData.additionalInfo || 'None'}</td>
      </tr>
    </table>
  </div>
  <div style="background: #f9fafb; padding: 16px 30px; border-radius: 0 0 12px 12px; border: 1px solid #e5e7eb; border-top: none; text-align: center;">
    <p style="color: #9ca3af; margin: 0; font-size: 12px;">Motive Home Care — ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
  </div>
</div>`,
          reply_to: formData.contactEmail,
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'agency_application_submit', {
          hear_about_us: formData.hearAboutUs,
          agency_name: formData.agencyName
        });
      }

      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error sending form:', error);
      alert('There was an error submitting your application. Please try again or contact us directly at (213) 495-0092.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="agencies-join">
        <div className="container">
          <div className="success-screen">
            <div className="success-animation">
              <div className="success-circle">
                <div className="success-ripples">
                  <div className="ripple"></div>
                  <div className="ripple"></div>
                  <div className="ripple"></div>
                </div>
                <div className="checkmark">
                  <svg viewBox="0 0 52 52">
                    <circle cx="26" cy="26" r="25" fill="none"/>
                    <path fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8"/>
                  </svg>
                </div>
              </div>
            </div>
            
            <div className="success-content">
              <div className="success-badge">
                <span className="badge-sparkle">✨</span>
                Application Received
              </div>
              <h1>Thank You, {formData.agencyName}</h1>
              <p className="success-subtitle">
                We appreciate your interest in partnering with us. Your application has been received, and we look forward to exploring how we can work together to serve your community with excellence.
              </p>
              
              <div className="success-metrics">
                <div className="metric-card">
                  <div className="metric-icon">⏱️</div>
                  <div className="metric-content">
                    <span className="metric-value">Within 2 Hours</span>
                    <span className="metric-label">We'll reach out to you</span>
                  </div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-icon">☕</div>
                  <div className="metric-content">
                    <span className="metric-value">Professional Discussion</span>
                    <span className="metric-label">Focused on your needs</span>
                  </div>
                </div>
                
                <div className="metric-card">
                  <div className="metric-icon">🎯</div>
                  <div className="metric-content">
                    <span className="metric-value">Tailored Solutions</span>
                    <span className="metric-label">Designed for your agency</span>
                  </div>
                </div>
              </div>
              
              <div className="success-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => window.location.href = '/'}
                >
                  <span className="btn-icon">🏠</span>
                  Return to Homepage
                </button>
                <a href="tel:+12134950092" className="btn btn-secondary">
                  <span className="btn-icon">📞</span>
                  Call Us: (213) 495-0092
                </a>
              </div>

              <div className="success-note">
                <p>🤝 We are committed to supporting your agency's mission with the same dedication you show your patients every day.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="agencies-join">
      {/* Hero Section */}
      <section className={`hero ${isVisible ? 'hero--visible' : ''}`}>
        <div className="hero-background">
          <div className="floating-elements">
            <div className="floating-element"></div>
            <div className="floating-element"></div>
            <div className="floating-element"></div>
            <div className="floating-element"></div>
            <div className="floating-element"></div>
            <div className="floating-element"></div>
          </div>
          <div className="gradient-overlay"></div>
        </div>
        
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title hero-title--enhanced">
              Together, We Strengthen
              <span className="gradient-text"> Home health Communities</span>
            </h1>

            <p className="hero-subtitle">
              Same-day access to licensed <span className="hero-abbrev">PT</span>, <span className="hero-abbrev">OT</span> & <span className="hero-abbrev">ST</span> professionals across Southern California.
            </p>

            <div className="hero-cta-container">
              <button
                onClick={() => {
                  const formSection = document.querySelector('.form-section');
                  formSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn btn-primary btn-hero-cta"
              >
                Start Partnership Application
              </button>

              <a href="/#/therapists/apply" className="therapist-link">
                Are you a therapist? <span className="therapist-link-separator">—</span> <span className="therapist-link-cta">Join our team</span> <span className="therapist-link-arrow">→</span>
              </a>
            </div>

            <div className="journey-steps">
              <div className={`step ${heroActiveStep === 1 ? 'step--active' : ''}`}>
                <div className="step-indicator">
                  <div className="step-number">1</div>
                  <div className="step-pulse"></div>
                </div>
                <div className="step-content">
                  <span className="step-title">Share Your Information</span>
                  <span className="step-subtitle">Tell us about your agency</span>
                </div>
              </div>

              <div className="step-connector">
                <div className="connector-line"></div>
                <div className="connector-flow"></div>
              </div>

              <div className={`step ${heroActiveStep === 2 ? 'step--active' : ''}`}>
                <div className="step-indicator">
                  <div className="step-number">2</div>
                  <div className="step-pulse"></div>
                </div>
                <div className="step-content">
                  <span className="step-title">Additional Details</span>
                  <span className="step-subtitle">Help us understand your needs</span>
                </div>
              </div>

              <div className="step-connector">
                <div className="connector-line"></div>
                <div className="connector-flow"></div>
              </div>

              <div className={`step ${heroActiveStep === 3 ? 'step--active' : ''}`}>
                <div className="step-indicator">
                  <div className="step-number">3</div>
                  <div className="step-pulse"></div>
                </div>
                <div className="step-content">
                  <span className="step-title">Begin Partnership</span>
                  <span className="step-subtitle">Start working together</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="form-section">
        <div className="container">
          <div className="form-layout">
            
            <div className={`form-content ${animationStep ? 'form-content--visible' : ''}`}>
              {currentStep === 1 ? (
                <div className="form-card">
                  <div className="form-header">
                    <div className="step-microcopy">Step 1 to 2 • Takes about 2 minutes</div>
                    <h2>Let's Connect</h2>
                    <p>We'd like to learn more about your home health agency and how we might work together to better serve your community. Please share some basic information to get started.</p>
                  </div>

                  <div className="form-body">
                    <div className="form-row">
                      <div className="form-group agency-name-group">
                        <label htmlFor="agencyName">
                          <span className="label-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 21h18"/>
                              <path d="M5 21V7l8-4v18"/>
                              <path d="M19 21V11l-6-4"/>
                              <path d="M9 9v.01"/>
                              <path d="M9 12v.01"/>
                              <path d="M9 15v.01"/>
                              <path d="M9 18v.01"/>
                            </svg>
                          </span>
                          Business Name
                        </label>
                        <input
                          type="text"
                          id="agencyName"
                          value={formData.agencyName}
                          onChange={(e) => handleInputChange('agencyName', e.target.value)}
                          placeholder="Your business name"
                          className={errors.agencyName ? 'input--error' : ''}
                        />
                        {errors.agencyName && <span className="error-message">{errors.agencyName}</span>}
                      </div>
                      
                      <div className="form-group facility-type-group">
                        <label>
                          <span className="label-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"/>
                              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                              <path d="M12 17h.01"/>
                            </svg>
                          </span>
                          How did you hear about us?
                        </label>
                        <div className={`custom-select ${dropdownOpen ? 'custom-select--open' : ''} ${errors.hearAboutUs ? 'custom-select--error' : ''}`}>
                          <div
                            className="custom-select__trigger"
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                          >
                            <span className={formData.hearAboutUs ? 'custom-select__value' : 'custom-select__placeholder'}>
                              {formData.hearAboutUs ? (
                                <>
                                  <span className="custom-select__selected-icon" style={{ color: hearAboutUsOptions.find(t => t.value === formData.hearAboutUs)?.color }}>
                                    {hearAboutUsOptions.find(t => t.value === formData.hearAboutUs)?.icon}
                                  </span>
                                  {formData.hearAboutUs}
                                </>
                              ) : 'Select an option'}
                            </span>
                            <svg className="custom-select__arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="m6 9 6 6 6-6"/>
                            </svg>
                          </div>
                          {dropdownOpen && (
                            <div className="custom-select__dropdown">
                              {hearAboutUsOptions.map(type => (
                                <div
                                  key={type.value}
                                  className={`custom-select__option ${formData.hearAboutUs === type.value ? 'custom-select__option--selected' : ''}`}
                                  onClick={() => {
                                    handleInputChange('hearAboutUs', type.value);
                                    if (type.value !== 'Other') {
                                      handleInputChange('hearAboutUsOther', '');
                                    }
                                    setDropdownOpen(false);
                                  }}
                                >
                                  <span className="custom-select__option-icon" style={{ color: type.color }}>{type.icon}</span>
                                  <div className="custom-select__option-content">
                                    <span className="custom-select__option-title">{type.value}</span>
                                    <span className="custom-select__option-desc">{type.description}</span>
                                  </div>
                                  {formData.hearAboutUs === type.value && (
                                    <svg className="custom-select__check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={type.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                      <polyline points="20 6 9 17 4 12"/>
                                    </svg>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {errors.hearAboutUs && <span className="error-message">{errors.hearAboutUs}</span>}
                        {formData.hearAboutUs === 'Other' && (
                          <input
                            type="text"
                            value={formData.hearAboutUsOther}
                            onChange={(e) => handleInputChange('hearAboutUsOther', e.target.value)}
                            placeholder="Please specify..."
                            className={`other-specify-input ${errors.hearAboutUsOther ? 'input--error' : ''}`}
                            style={{ marginTop: '0.75rem' }}
                          />
                        )}
                        {errors.hearAboutUsOther && <span className="error-message">{errors.hearAboutUsOther}</span>}
                      </div>
                    </div>

                    <div className="form-row">
                      <div className="form-group email-group">
                        <label htmlFor="contactEmail">
                          <span className="label-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="2" y="4" width="20" height="16" rx="2"/>
                              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                            </svg>
                          </span>
                          Contact Email
                        </label>
                        <input
                          type="email"
                          id="contactEmail"
                          value={formData.contactEmail}
                          onChange={(e) => handleInputChange('contactEmail', e.target.value)}
                          placeholder="your.email@agency.com"
                          className={errors.contactEmail ? 'input--error' : ''}
                        />
                        {errors.contactEmail && <span className="error-message">{errors.contactEmail}</span>}
                      </div>
                      
                      <div className="form-group phone-group">
                        <label htmlFor="phone">
                          <span className="label-icon">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                            </svg>
                          </span>
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                          placeholder="(213) 555-0123"
                          className={errors.phone ? 'input--error' : ''}
                        />
                        {errors.phone && <span className="error-message">{errors.phone}</span>}
                      </div>
                    </div>
                  </div>

                  <div className="form-footer">
                    <button
                      onClick={handleStep1Submit}
                      className="btn btn-primary btn-large"
                    >
                      <span className="btn-icon">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14"/>
                          <path d="m12 5 7 7-7 7"/>
                        </svg>
                      </span>
                      Continue to Step 2
                      <div className="btn-shine"></div>
                    </button>
                    
                    <div className="security-note">
                      <span className="security-icon">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                          <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                        </svg>
                      </span>
                      <span>Your information is secure and will be kept confidential.</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="form-card">
                  <div className="form-header">
                    <div className="header-icon">
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                        <polyline points="14 2 14 8 20 8"/>
                        <line x1="16" y1="13" x2="8" y2="13"/>
                        <line x1="16" y1="17" x2="8" y2="17"/>
                        <line x1="10" y1="9" x2="8" y2="9"/>
                      </svg>
                    </div>
                    <h2>Additional Information</h2>
                    <p>Please share any additional details about your agency, specific needs, or questions you may have. This helps us prepare for our conversation and better understand how we can assist you.</p>
                  </div>

                  <div className="form-body">
                    <div className="form-group message-group">
                      <label htmlFor="additionalInfo">
                        <span className="label-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                          </svg>
                        </span>
                        Tell us more about your agency and needs (Optional)
                      </label>
                      <textarea
                        id="additionalInfo"
                        value={formData.additionalInfo}
                        onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                        placeholder="Share any specific information about your agency, current challenges, or how you envision a partnership might benefit your patients and staff..."
                        rows="3"
                        style={{
                          resize: 'vertical',
                          minHeight: '80px'
                        }}
                      />
                    </div>

                    <div className="form-group message-group">
                      <label htmlFor="staffingAreas">
                        <span className="label-icon">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                            <circle cx="12" cy="10" r="3"/>
                          </svg>
                        </span>
                        What are the areas in which you need therapy staffing? (Optional)
                      </label>
                      <textarea
                        id="staffingAreas"
                        value={formData.staffingAreas}
                        onChange={(e) => handleInputChange('staffingAreas', e.target.value)}
                        placeholder="e.g., Los Angeles, Orange County, San Fernando Valley..."
                        rows="3"
                        style={{
                          resize: 'vertical',
                          minHeight: '80px'
                        }}
                      />
                    </div>

                  </div>

                  <div className="form-footer">
                    <div className="form-actions">
                      <button 
                        onClick={() => setCurrentStep(1)}
                        className="btn btn-outline"
                      >
                        <span className="btn-icon">←</span>
                        Previous Step
                      </button>
                      
                      <button 
                        onClick={handleFinalSubmit}
                        className="btn btn-primary btn-large"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="spinner"></div>
                            Submitting Application...
                          </>
                        ) : (
                          <>
                            <span className="btn-icon">✉️</span>
                            Submit Application
                            <div className="btn-shine"></div>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

          </div>
        </div>
      </section>


      {/* Call to Action Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <div className="cta-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
              </svg>
              Partner With Us
            </div>
            <h2>Your Community Deserves Excellence</h2>
            <p>Every home health agency plays a vital role in community well-being. Let's explore how we can work together to enhance the care you provide.</p>

            <div className="cta-actions">
              <button
                onClick={() => {
                  const formSection = document.querySelector('.form-section');
                  formSection?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="cta-btn cta-btn--primary"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                </svg>
                Start Partnership Application
              </button>

              <a href="tel:+12134950092" className="cta-btn cta-btn--secondary">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                Call Our Team
              </a>
            </div>

            <div className="cta-trust">
              <div className="cta-trust__item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                </svg>
                Trusted Partner
              </div>
              <div className="cta-trust__item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
                Same-Day Support
              </div>
              <div className="cta-trust__item">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Licensed Clinicians
              </div>
            </div>

            <a href="/#/therapists/apply" className="cta-therapist-link">
              <span className="cta-therapist-link__text">Are you a therapist looking for opportunities?</span>
              <span className="cta-therapist-link__cta">Join our team <span className="cta-therapist-link__arrow">→</span></span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AgenciesJoin;